import { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function InlineRename({ value, onSave, disabled, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    if (disabled) return;
    setEditValue(value);
    setEditing(true);
  };

  const cancel = (e) => {
    e?.stopPropagation();
    setEditing(false);
    setEditValue(value);
  };

  const save = async (e) => {
    e?.stopPropagation();
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === value) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch {
      // parent handles toast
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') save(e);
    if (e.key === 'Escape') cancel(e);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-7 text-sm px-2 py-1 w-full min-w-[100px]"
          disabled={saving}
          data-testid="inline-rename-input"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={save}
          disabled={saving}
          className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 shrink-0"
          data-testid="inline-rename-save"
        >
          <Check className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={cancel}
          disabled={saving}
          className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-50 shrink-0"
          data-testid="inline-rename-cancel"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 group ${className}`}>
      <span className="truncate">{value}</span>
      {!disabled && (
        <button
          onClick={startEdit}
          // Always-visible pencil. Previous design used `opacity-0
          // group-hover:opacity-100` which (a) was undiscoverable on desktop
          // unless users happened to hover the exact parent span and (b)
          // never appeared on touch devices that don't trigger :hover. Now
          // it's always faintly visible (opacity-60) and brightens on hover
          // — a clear "you can rename this" affordance everywhere.
          className="opacity-60 hover:opacity-100 transition-opacity p-1 rounded hover:bg-cyan-50 shrink-0"
          title="Rename"
          data-testid="inline-rename-btn"
        >
          <Pencil className="w-3.5 h-3.5 text-cyan-600 hover:text-cyan-700" />
        </button>
      )}
    </span>
  );
}

export function InlineRenameDouble({ value1, value2, label1 = 'Unit', label2 = 'Batch Code', onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [editVal1, setEditVal1] = useState(value1);
  const [editVal2, setEditVal2] = useState(value2);
  const [saving, setSaving] = useState(false);
  const input1Ref = useRef(null);

  useEffect(() => {
    if (editing && input1Ref.current) {
      input1Ref.current.focus();
      input1Ref.current.select();
    }
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    if (disabled) return;
    setEditVal1(value1);
    setEditVal2(value2);
    setEditing(true);
  };

  const cancel = (e) => {
    e?.stopPropagation();
    setEditing(false);
  };

  const save = async (e) => {
    e?.stopPropagation();
    const t1 = editVal1.trim();
    const t2 = editVal2.trim();
    if (t1 === value1 && t2 === value2) { cancel(); return; }
    if (!t1 && !t2) { cancel(); return; }
    setSaving(true);
    try {
      const updates = {};
      if (t1 !== value1 && t1) updates.unit = t1;
      if (t2 !== value2 && t2) updates.batch_code = t2;
      if (Object.keys(updates).length === 0) { cancel(); return; }
      await onSave(updates);
      setEditing(false);
    } catch {
      // parent handles toast
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') save(e);
    if (e.key === 'Escape') cancel(e);
  };

  if (!editing) {
    if (disabled) return null;
    return (
      <button
        onClick={startEdit}
        disabled={disabled}
        // Always-visible pencil (matches single InlineRename — see comment
        // there). Was opacity-0 + group-hover, undiscoverable on touch.
        className="opacity-60 hover:opacity-100 transition-opacity p-1 rounded hover:bg-cyan-50 shrink-0"
        title="Rename"
        data-testid="inline-rename-btn"
      >
        <Pencil className="w-3.5 h-3.5 text-cyan-600 hover:text-cyan-700" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center" onClick={cancel}>
      <div className="bg-white rounded-lg shadow-lg p-4 min-w-[280px] space-y-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-medium text-gray-700">Rename Record</p>
        <div className="space-y-2">
          <div>
            <label className="text-xs text-gray-500">{label1}</label>
            <Input
              ref={input1Ref}
              value={editVal1}
              onChange={(e) => setEditVal1(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-8 text-sm"
              disabled={saving}
              data-testid="inline-rename-unit"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{label2}</label>
            <Input
              value={editVal2}
              onChange={(e) => setEditVal2(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-8 text-sm"
              disabled={saving}
              data-testid="inline-rename-batch"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" type="button" onClick={cancel} disabled={saving}>Cancel</Button>
          <Button size="sm" type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); save(e); }} disabled={saving} className="bg-[#0891b2] hover:bg-[#0e7490] text-white" data-testid="inline-rename-double-save">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
